const moment = require('moment');
const { isArray } = require('@/functions/is');

/**
 * @param value
 * @return {boolean}
 */
const toBool = (value) => {
    // In JS !!"false" and !!"0" = true because they're non-empty strings.
    // Handle those special cases.
    if (value === '0' || value === 'false' || value === 'no' || value === 'off') {
        return false;
    }

    return !!value;
};

/**
 * @param value
 * @return {?boolean}
 */
const toNullableBool = (value) => (value === null ? null : toBool(value));

/**
 * Convert true/false/null to 1/0/null
 *
 * @param {boolean|null} value
 *
 * @returns {number|null}
 */
const toNullableBoolInt = (value) => {
    if (value === null) {
        return null;
    }

    return toBool(value) ? 1 : 0;
};

/**
 * @param value
 * @return {?Date}
 */
const toDate = (value) => {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return value;
    }

    if (typeof value === 'string') {
        let d;

        if (value.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/)) {
            // This is a Y-m-d H:i:s date and causes issues in Safari. But we can use moment to parse it
            // and tell it the format.
            d = moment(value, 'YYYY-MM-DD hh:mm:ss').toDate();
        } else {
            d = moment(value).toDate();
        }

        // Check for invalid dates.
        if (!d || Number.isNaN(d.getTime())) {
            return null;
        }

        return d;
    }

    return null;
};

/**
 * @param value
 * @return {?Date}
 */
const toEndOfDayDate = (value) => {
    if (!value) {
        return null;
    }

    const date = toDate(value);
    if (date) {
        return moment(value).endOf('day').toDate();
    }

    return null;
};

/**
 * @param value
 * @return {number}
 */
const toFloat = (value) => {
    if (!value) {
        return 0.00;
    }

    return parseFloat(value);
};

/**
 * @param value
 * @return {?number}
 */
const toNullableFloat = (value) => (value === null ? null : toFloat(value));

/**
 * @param value
 * @return {number}
 */
const toInt = (value) => {
    if (!value) {
        return 0;
    }

    return parseInt(value, 10);
};

/**
 * @param value
 * @return {?number}
 */
const toNullableInt = (value) => (value === null ? null : toInt(value));

/**
 * @param value
 * @return {number[]}
 */
const toIntArray = (value) => {
    if (!value) {
        return [];
    }

    let values = [];
    if (isArray(value)) {
        values = value;
    } else {
        values = String(value).split(',');
    }

    return values.map(toInt).filter((i) => !!i);
};

/**
 * @param value
 * @return {?number[]}
 */
const toNullableIntArray = (value) => (value === null ? null : toIntArray(value));

/**
 * @param value
 * @return {string[]}
 */
const toStringArray = (value) => {
    if (!value) {
        return [];
    }

    let values = [];
    if (isArray(value)) {
        values = value;
    } else {
        values = String(value).split(',');
    }

    return values.map(toString).filter((s) => !!s);
};

/**
 * @param value
 * @return {string}
 */
const toString = (value) => {
    if (!value) {
        return '';
    }

    if ((typeof value === 'object') && !(value instanceof Array)) {
        return JSON.stringify(value);
    }

    return String(value);
};

/**
 * @param value
 * @return {?string}
 */
const toNullableString = (value) => (value === null ? null : toString(value));

/**
 * Casts to a string.
 * Returns null if the trimmed version of the string is empty.
 *
 * @param value
 * @return {?string}
 */
const toNullableTrimmedString = (value) => toNullableString(value)?.trim() || null;

/**
 * @param {string|{}} value
 * @return {{}}
 */
const fromJson = (value) => {
    if (typeof value === 'string') {
        try {
            return JSON.parse(value);
        } catch (e) {
            console.error('Failed to parse JSON', value);
            return null;
        }
    }

    return value;
};

module.exports = {
    toBool,
    toNullableBool,
    toDate,
    toEndOfDayDate,
    toFloat,
    toNullableFloat,
    toInt,
    toNullableInt,
    toNullableBoolInt,
    toIntArray,
    toNullableIntArray,
    toString,
    toNullableString,
    toStringArray,
    toNullableTrimmedString,
    fromJson,
};
